// ─── Tuning Constants ────────────────────────────────────────────────────────

export const KOMA_SPIN = {
    // Flight — Wind Rider: slow, floaty, dramatic wind curves
    FLIGHT_SPEED: 0.38,       // was 0.55 — slower base = wind has more relative effect
    FLIGHT_GRAVITY: 0.06,     // was 0.08 — floatier = more airtime for wind to act
    FLIGHT_RADIUS: 9,
    FLIGHT_DAMAGE: 10,    // S1 nerf: was 15
    FLIGHT_BLAST: 60,
    FLIGHT_KNOCKBACK: 0.05,
    FLIGHT_TIMEOUT: 360,      // was 300 — more time for wind-extended flights (6s)
    WIND_AMP: 2.5,            // was 1.0 — +2.5x on top of base 1.0x = 3.5x total

    // String unfurl
    UNFURL_POINTS: 3,
    UNFURL_PARTICLE_SIZE: 2,
};

export const KOMA_RIBBON = {
    MAX_POINTS: 18,          // ring buffer size (~0.3s at 60fps)
    WAVE_FREQ: 0.15,         // base wave frequency (rad/frame)
    WAVE_SPEED_SCALE: 0.02,  // additional freq per unit speed
    WAVE_AMP_HEAD: 2,        // flutter amplitude at head (px) — low so attachment stays stable
    WAVE_AMP_TAIL: 20,       // flutter amplitude at tail (px)
    WIND_PUSH: 120,          // wind-to-pixel displacement multiplier
    MAX_SEG: 12,             // max segment length before clamping
    SEG_SPEED_SCALE: 1.5,    // segment length scales with speed
    HEAD_HALF_WIDTH: 0.8,    // thin at attachment = pennant look
    TAIL_HALF_WIDTH: 3.0,    // wider trailing end = streamer
    BOUNCE_TRIM: 6,          // points retained on Skipper bounce
};

/** Shared ribbon point type for Koma trail */
export interface RibbonPoint {
    x: number;
    y: number;
    flutter: number;
    age: number;
}

/** Update ribbon buffer — push current pos, pop excess, compute flutter + wind */
export function updateKomaRibbon(
    ribbon: RibbonPoint[],
    x: number,
    y: number,
    flightFrames: number,
    speed: number,
    wind: number,
): void {
    // Push new point at front
    ribbon.unshift({ x, y, flutter: 0, age: 0 });

    // Pop excess
    while (ribbon.length > KOMA_RIBBON.MAX_POINTS) ribbon.pop();

    // Compute flutter for each point
    const freq = KOMA_RIBBON.WAVE_FREQ + speed * KOMA_RIBBON.WAVE_SPEED_SCALE;
    for (let i = 0; i < ribbon.length; i++) {
        const t = ribbon.length > 1 ? i / (ribbon.length - 1) : 0;
        const amp = KOMA_RIBBON.WAVE_AMP_HEAD + (KOMA_RIBBON.WAVE_AMP_TAIL - KOMA_RIBBON.WAVE_AMP_HEAD) * t;
        const wave = Math.sin(flightFrames * freq - t * Math.PI * 2) * amp;
        const windPush = wind * KOMA_RIBBON.WIND_PUSH * t * t;
        ribbon[i].flutter = i === 0 ? 0 : wave + windPush;
        ribbon[i].age = i;
    }

    // Segment length clamping — pull points toward predecessor
    const maxSeg = KOMA_RIBBON.MAX_SEG + speed * KOMA_RIBBON.SEG_SPEED_SCALE;
    for (let i = 1; i < ribbon.length; i++) {
        const dx = ribbon[i].x - ribbon[i - 1].x;
        const dy = ribbon[i].y - ribbon[i - 1].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxSeg) {
            const ratio = maxSeg / dist;
            ribbon[i].x = ribbon[i - 1].x + dx * ratio;
            ribbon[i].y = ribbon[i - 1].y + dy * ratio;
        }
    }
}
